import { FEDERATED_TOKEN_MAX_TTL_SECONDS, doorPullDataPlaneIdSchema } from "@zion/sdk-contracts";
//#region src/door-pull.ts
/**
* The release-engine operator API route on which the operator program enrolls its door key:
* `POST /v1/operator/data-planes/:dpId/door-pull-key`. The route sits under the operator API prefix,
* so `OperatorMtlsGuard` checks that the client certificate CN equals `:dpId`, and
* `ApprovedDpIdGuard` checks that the certificate is approved and not revoked. A request for another
* data plane answers 403 before the body is read (F4).
*
* Answers:
* - 201 with `outcome: 'enrolled'` when release-engine stored the key as the data plane's one
*   enrolled key, in place of any other key it held for that data plane;
* - 200 with `outcome: 'unchanged'` when it held that key already;
* - 400 for a body that fails `DoorPullKeyEnrollmentRequestSchema` or a `:dpId` that is not a
*   lower-case UUID;
* - 401 or 403 from the two guards.
*
* Every write appends one audit row that names the data plane id and the key id (F10).
*/
const DOOR_PULL_KEY_ENROLLMENT_PATH = "/v1/operator/data-planes/:dpId/door-pull-key";
/**
* The concrete enrollment path of `dataPlaneId`: {@link DOOR_PULL_KEY_ENROLLMENT_PATH} with `:dpId`
* replaced.
*
* Errors: throws `TypeError` when `dataPlaneId` is not a lower-case UUID, so the operator program
* never posts to a path the guard cannot match. Pure.
*/
function doorPullKeyEnrollmentPath(dataPlaneId) {
	if (!doorPullDataPlaneIdSchema.safeParse(dataPlaneId).success) throw new TypeError("doorPullKeyEnrollmentPath: the data plane id must be a lower-case UUID");
	return DOOR_PULL_KEY_ENROLLMENT_PATH.replace(":dpId", dataPlaneId);
}
/** The outcomes of the enrollment route: the key was stored, or release-engine held it already. */
const DOOR_PULL_KEY_ENROLLMENT_OUTCOMES = ["enrolled", "unchanged"];
/**
* The most namespaces the refresher writes its pull Secret into (I4). The DP chart's namespace list
* holds at most this many entries; the report refuses a larger count.
*/
const DOOR_PULL_NAMESPACES_MAX = 64;
/**
* Why the refresher's last attempt failed, in the order of its steps:
*
* - `kms-public-key`: the KMS `GetPublicKey` call failed, or its answer was not a P-256 key.
* - `kms-sign`: the KMS `Sign` call failed (a disabled or deleted key, a missing sign right), or its
*   answer was not a DER ECDSA signature of two 32-byte integers.
* - `enrollment-refused`: release-engine answered the enrollment with a status other than 2xx, with
*   a 2xx whose body is malformed, or with a 2xx that names another key id.
* - `enrollment-unreachable`: release-engine did not answer the enrollment.
* - `exchange-refused`: the exchange at `POST /v1/auth/token/federated` gave no token for a reason
*   other than transport (`exchange-unreachable`) or a malformed answer (`exchange-malformed`).
*   That covers a status other than 200, an assertion that the refresher already spent, and a
*   request that the exchange client refused before it sent it (ruling R-019). Before
*   provisioning-svc registers the key, every exchange answers this.
* - `exchange-unreachable`: cp-auth-api did not answer the exchange.
* - `exchange-malformed`: cp-auth-api answered 200 with a body that fails
*   `federatedTokenResponseSchema`.
* - `secret-write`: the pull Secret failed in at least one namespace: its read failed, it held a type
*   other than `kubernetes.io/dockerconfigjson`, or its write failed (ruling R-019).
*/
const DOOR_PULL_REPORT_ERRORS = [
	"kms-public-key",
	"kms-sign",
	"enrollment-refused",
	"enrollment-unreachable",
	"exchange-refused",
	"exchange-unreachable",
	"exchange-malformed",
	"secret-write"
];
/**
* The least time a self-minted token must still live for the CP to treat it as fresh: 120 s.
*
* The bound sits below the operator program's refresh lead of 300 s (`DOOR_PULL_REFRESH_LEAD_MS`,
* on a 60 s check). The refresher replaces the token with 240 s to 300 s left, and the report with
* the new expiry reaches the CP within one 30 s poll and one 60 s loop tick. So a running refresher
* always reads fresh, and the CP sends no bootstrap token to its CP mirror Secret `zion-door-pull`
* while the refresher writes `zion-door-pull-self` (ruling R-003).
*
* A stopped refresher reads stale 120 s before its token expires. That leaves the 60 s tick of
* `DoorPullTokenLoop`, the 10 s exchange timeout and 30 s of clock skew before the token dies.
*/
const DOOR_PULL_SELF_MINT_MIN_REMAINING_MS = 12e4;
/**
* The most time a self-minted token can still live: the 900 s token life
* (`FEDERATED_TOKEN_MAX_TTL_SECONDS`) plus 30 s of clock skew, the skew the CP's assertion check
* allows. A report whose token outlives this came from a skewed or broken clock, and the CP does not
* trust it.
*/
const DOOR_PULL_SELF_MINT_MAX_REMAINING_MS = FEDERATED_TOKEN_MAX_TTL_SECONDS * 1e3 + 3e4;
/**
* Whether a data plane holds a fresh self-minted pull token, so provisioning-svc's
* `DoorPullTokenLoop` skips the bootstrap mint for it (D8).
*
* Input: the `doorPull` member the CP stored from the data plane's last status report (`null` or
* `undefined` when it stored none), and the CP clock in milliseconds since the epoch.
*
* Output: `true` only when all four hold:
* 1. the report is present and `tokenExpiresAt` is not `null`;
* 2. `namespacesTotal` is at least 1 and `namespacesWritten` equals it, so every namespace on the
*    list holds the newest token;
* 3. the token still lives at least {@link DOOR_PULL_SELF_MINT_MIN_REMAINING_MS} after `nowMs`;
* 4. it lives at most {@link DOOR_PULL_SELF_MINT_MAX_REMAINING_MS} after `nowMs`.
*
* `false` for every other input. A report that stops arriving goes stale on its own: the stored
* `tokenExpiresAt` stays, and within 810 s rule 3 fails: rule 4 admits at most 930 s of remaining
* life, and rule 3 fails below 120 s. `lastError` does not decide: a token that
* still lives is still fresh after one failed refresh.
*
* Never throws. Pure: the clock is an argument.
*/
function isDoorPullSelfMintFresh(report, nowMs) {
	if (report === null || report === void 0 || report.tokenExpiresAt === null) return false;
	if (report.namespacesTotal < 1 || report.namespacesWritten !== report.namespacesTotal) return false;
	const remainingMs = Date.parse(report.tokenExpiresAt) - nowMs;
	return remainingMs >= 12e4 && remainingMs <= DOOR_PULL_SELF_MINT_MAX_REMAINING_MS;
}
//#endregion
export { DOOR_PULL_KEY_ENROLLMENT_OUTCOMES, DOOR_PULL_KEY_ENROLLMENT_PATH, DOOR_PULL_NAMESPACES_MAX, DOOR_PULL_REPORT_ERRORS, DOOR_PULL_SELF_MINT_MAX_REMAINING_MS, DOOR_PULL_SELF_MINT_MIN_REMAINING_MS, doorPullKeyEnrollmentPath, isDoorPullSelfMintFresh };

//# sourceMappingURL=door-pull.js.map